import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from '../../../components/PageHeader';
import Button from '../../../components/ui/Button';
import { useModuleLicense } from '../../../hooks/useModuleLicense';
import pbrService from '../../../services/pbr';
import PbrStageBadge from '../../../components/cultivar/pbr/PbrStageBadge';
import DeadlineChip from '../../../components/cultivar/pbr/DeadlineChip';
import type { PbrApplication, PbrStage } from '../../../types/cultivar';
import { PBR_STAGE_LABELS } from '../../../types/cultivar';
import { FileCheck, Plus, ShieldAlert } from 'lucide-react';

export default function PbrApplications() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasAccess, loading: licLoading } = useModuleLicense('cultivar');

  const [rows, setRows] = useState<PbrApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState<PbrStage | ''>(
    (searchParams.get('stage') as PbrStage) || ''
  );
  const [jurisdiction, setJurisdiction] = useState<string>(searchParams.get('jurisdiction') || '');
  const cultivarId = searchParams.get('cultivar_id') ? Number(searchParams.get('cultivar_id')) : null;

  useEffect(() => {
    if (!hasAccess) { setLoading(false); return; }
    setLoading(true);
    pbrService
      .listApplications({
        cultivarId: cultivarId || undefined,
        stage: stage || undefined,
        jurisdiction: jurisdiction || undefined,
      })
      .then(r => { if (r.success && r.data) setRows(r.data); })
      .finally(() => setLoading(false));
  }, [hasAccess, stage, jurisdiction, cultivarId]);

  useEffect(() => {
    const p = new URLSearchParams();
    if (stage) p.set('stage', stage);
    if (jurisdiction) p.set('jurisdiction', jurisdiction);
    if (cultivarId) p.set('cultivar_id', String(cultivarId));
    setSearchParams(p, { replace: true });
  }, [stage, jurisdiction, cultivarId, setSearchParams]);

  if (licLoading) return <div className="p-6 text-neutral-500">Loading…</div>;
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ShieldAlert className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-neutral-700">Access Restricted</h2>
        </div>
      </div>
    );
  }

  const stages: PbrStage[] = [
    'candidate', 'part1_prep', 'part1_submitted', 'accepted',
    'trial_in_progress', 'part2_prep', 'part2_submitted',
    'granted', 'ceased', 'rejected', 'withdrawn',
  ];
  const jurisdictions = ['AU', 'US', 'EU', 'NZ', 'INT', 'JP', 'KR', 'ZA', 'CL', 'OTHER'];

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="PBR Applications"
        subtitle="All Plant Breeder's Rights applications"
        icon={<FileCheck className="h-6 w-6 text-white" />}
        actionButtons={
          <Button onClick={() => navigate('/cultivar/pbr/applications/new')}>
            <Plus className="h-4 w-4 mr-1" /> New application
          </Button>
        }
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-4">
        <div className="bg-white border border-neutral-200 rounded-lg p-4 flex flex-wrap gap-4">
          <select
            value={stage}
            onChange={e => setStage(e.target.value as PbrStage | '')}
            className="input-professional"
          >
            <option value="">All stages</option>
            {stages.map(s => <option key={s} value={s}>{PBR_STAGE_LABELS[s]}</option>)}
          </select>
          <select
            value={jurisdiction}
            onChange={e => setJurisdiction(e.target.value)}
            className="input-professional"
          >
            <option value="">All jurisdictions</option>
            {jurisdictions.map(j => <option key={j} value={j}>{j}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="text-neutral-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
            <p className="text-neutral-600">No applications yet.</p>
          </div>
        ) : (
          <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-neutral-600 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Cultivar</th>
                  <th className="px-4 py-3 font-medium">Jurisdiction</th>
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 font-medium">Stage</th>
                  <th className="px-4 py-3 font-medium">Part 1 deadline</th>
                  <th className="px-4 py-3 font-medium">QP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {rows.map(a => (
                  <tr
                    key={a.id}
                    className="hover:bg-neutral-50 cursor-pointer"
                    onClick={() => navigate(`/cultivar/pbr/applications/${a.id}`)}
                  >
                    <td className="px-4 py-3 font-medium">{a.cultivar_trade_name || a.cultivar_name}</td>
                    <td className="px-4 py-3">{a.jurisdiction}</td>
                    <td className="px-4 py-3">{a.application_type}</td>
                    <td className="px-4 py-3"><PbrStageBadge stage={a.stage} /></td>
                    <td className="px-4 py-3"><DeadlineChip deadline={a.part1_due_date} label="Due" /></td>
                    <td className="px-4 py-3">{a.qualified_person_name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
